import { Empty, Flex } from '@byecode/ui'
import type { EmptyViewBreakPointConfigProtocol, RichTextContentProtocol, VariableADTvalue } from '@lighthouse/core'
import { EMPTY_VIEW_IMAGE_MODE } from '@lighthouse/core'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useApplicationContext } from '../../contexts'
import { getTextColorStyle } from '../../utils/design'
import { fieldConvertValue } from '../../utils/helper'
import { getImageFullUrlInApplication } from '../../utils/image'
import { useAppThemeContext } from '../ApplicationContainer'
import { EmptyNoRecordSvg } from '../EmptyNoRecordSvg'
import { useFlowLayoutContext } from '../LayoutEngine/Context'

export interface ViewEmptyStateProps {
    /** 空状态配置 */
    config?: EmptyViewBreakPointConfigProtocol['emptyView']
    onRenderRichText: (v: RichTextContentProtocol) => string

    /** 锚点 id，用于滚动定位 */
    id?: string
    /** 自定义样式 */
    style?: React.CSSProperties
    /** 自定义根样式 */
    rootStyle?: React.CSSProperties
}

const EMPTY_IMAGE_SHORT_SIDE_MAX = 140

type EmptyImageSize = {
    width: number
    height: number
}

const getCustomEmptyImageSize = (width: number, height: number): EmptyImageSize => {
    const shortSide = Math.min(width, height)
    if (!shortSide || shortSide <= EMPTY_IMAGE_SHORT_SIDE_MAX) {
        return {
            width,
            height
        }
    }

    const scale = EMPTY_IMAGE_SHORT_SIDE_MAX / shortSide
    return {
        width: Math.round(width * scale),
        height: Math.round(height * scale)
    }
}

const CustomEmptyImage: React.FC<{ src: string }> = ({ src }) => {
    const [size, setSize] = useState<EmptyImageSize>()

    useEffect(() => {
        let disposed = false
        const image = new window.Image()

        setSize(undefined)
        image.onload = () => {
            if (disposed) {
                return
            }

            const { naturalWidth, naturalHeight } = image
            if (!naturalWidth || !naturalHeight) {
                return
            }

            setSize(getCustomEmptyImageSize(naturalWidth, naturalHeight))
        }
        image.src = src

        return () => {
            disposed = true
        }
    }, [src])

    if (!size) {
        return null
    }

    return <img src={src} alt="empty" width={size.width} height={size.height} style={{ display: 'block' }} />
}

/**
 * 视图空状态组件
 * 统一渲染所有视图组件的空状态
 */
export function ViewEmptyState(props: ViewEmptyStateProps) {
    const { config, onRenderRichText, id, style, rootStyle } = props
    const {
        theme: { palettes }
    } = useAppThemeContext()

    const { useParseVariableWithSubscription } = useFlowLayoutContext()
    const parseImageVariable = useParseVariableWithSubscription(config?.customImage)
    const parseColorVariable = useParseVariableWithSubscription(config?.titleColor)
    const { appId } = useApplicationContext()

    const parseBackgroundImageVariable = useCallback(
        (value: VariableADTvalue) => {
            const field = parseImageVariable(value)

            return field ? fieldConvertValue(field, { useFileUrl: true }) : ''
        },
        [parseImageVariable]
    )

    const parseBackgroundColorVariable = useCallback(
        (value: VariableADTvalue) => {
            const field = parseColorVariable(value)

            return field ? fieldConvertValue(field) : ''
        },
        [parseColorVariable]
    )

    const { title, titleColor, imageMode = EMPTY_VIEW_IMAGE_MODE.default, customImage } = config || {}

    // 文案：为空时不占空间（如果文案为空字符串，则不显示文案）
    const description = useMemo(() => {
        // 如果明确设置了空字符串，则不显示文案
        if (!title) {
            return
        }
        const titleStr = onRenderRichText(title)
        if (!titleStr) {
            return
        }
        // 如果有自定义文案，使用自定义文案
        if (titleStr && titleStr.trim()) {
            return titleStr
        }
    }, [title, onRenderRichText])

    // 文案颜色样式
    const titleStyle = useMemo(() => {
        if (!titleColor) {
            return {}
        }
        return getTextColorStyle(titleColor, palettes, parseBackgroundColorVariable)
    }, [titleColor, palettes, parseBackgroundColorVariable])

    // 渲染插画
    const renderImage = useMemo(() => {
        switch (imageMode) {
            case EMPTY_VIEW_IMAGE_MODE.hidden: {
                return null
            }
            case EMPTY_VIEW_IMAGE_MODE.custom: {
                if (!customImage) {
                    return null
                }
                const fullUrl = customImage.image
                    ? getImageFullUrlInApplication(appId, parseBackgroundImageVariable(customImage.image.resource))
                    : ''
                if (!fullUrl) {
                    return null
                }
                return <CustomEmptyImage src={fullUrl} />
            }

            default: {
                return <EmptyNoRecordSvg color="var(--color-app-main)" />
            }
        }
    }, [imageMode, customImage, appId, parseBackgroundImageVariable])

    // 如果文案为空且不显示图片，则不渲染任何内容
    const hasContent = description || renderImage
    if (!hasContent) {
        return null
    }

    return (
        <Flex id={id} alignItems="center" justifyContent="center" style={style}>
            <Empty
                styles={{
                    root: { padding: '16px 0', ...rootStyle },
                    content: { ...titleStyle }
                }}
                icon={renderImage}
                description={description}
            />
        </Flex>
    )
}

export default ViewEmptyState
